import { ArrowUpTrayIcon } from '@/components/icons/index';
import ConditionalRenderer from '@/components/ui/custom/ConditionalRenderer';
import Text from '@/components/ui/custom/dataDisplay/Text';
import { BaseInputProps } from '@/components/ui/custom/dataInput/BaseInput';
import { InputHTMLAttributes, PropsWithChildren, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { twMerge } from 'tailwind-merge';
import FileInput from './FileInput';

type FileInputZoneProps = Omit<
  InputHTMLAttributes<HTMLInputElement>,
  'value' | 'className' | 'onChange'
> &
  BaseInputProps & {
    onChange?(files: FileList | null): void;
    limit?: number;
    multiple?: boolean;
  };

export default function FileInputZone({
  className,
  label,
  labelPosition = 'top',
  errorLabelText,
  onChange,
  ...rest
}: FileInputZoneProps) {
  const [isDragging, setIsDragging] = useState(false);

  return (
    <FileInputZoneWrapper
      label={label}
      labelPosition={labelPosition}
      className={className}
      errorLabelText={errorLabelText}
      isDragging={isDragging}
    >
      <FileInput
        className="flex w-full grow opacity-0 !cursor-pointer"
        onChange={onChange}
        onDragging={setIsDragging}
        {...rest}
      />
    </FileInputZoneWrapper>
  );
}

export function FileInputZoneWrapper({
  children,
  label,
  labelPosition,
  isDragging,
  className,
  errorLabelText,
}: PropsWithChildren<
  Pick<
    FileInputZoneProps,
    'label' | 'labelPosition' | 'className' | 'errorLabelText'
  > & {
    isDragging: boolean;
  }
>) {
  const [t] = useTranslation('translation', {
    keyPrefix: 'common',
  });
  const borderClassName = twMerge(
    'border border-dashed transition-all',
    isDragging
      ? 'border-blue-500'
      : 'border-neutral-content hover:border-base-300',
  );

  return (
    <section
      className={twMerge(
        'flex w-full rounded-lg relative gap-1',
        labelPosition === 'left' ? '' : 'flex-col',
        className?.base,
      )}
    >
      <section
        className={twMerge(
          'flex grow rounded-lg min-h-20 transition-all duration-300 ease-in-out',
          borderClassName,
          className?.section,
        )}
      >
        <section className="flex flex-col gap-2.5 grow justify-center items-center text-base-content/70 font-500">
          <ArrowUpTrayIcon className="shrink-0" />
          <Text text={label || t('fileInstructions')} className="text-center" />
        </section>
        <section className="absolute flex w-full h-full bg-transparent">
          {children}
        </section>
      </section>
      <ConditionalRenderer condition={errorLabelText}>
        <label
          data-testid="errorLabel"
          className={twMerge(
            'label leading-none p-0 w-full text-error',
            className?.error,
          )}
        >
          {errorLabelText}
        </label>
      </ConditionalRenderer>
    </section>
  );
}
